import { differenceInYears } from 'date-fns';
import _ from 'lodash';
import {
    Address,
    ProviderGuarantor,
    ProviderPatient,
    ProviderPayer,
    ProviderSubscriber,
    ProviderSubscriberPatient,
    SupportGuarantor,
    SupportPatient,
    SupportPayer,
    SupportSubscriber,
    SupportSubscriberPatient,
} from '../../../api';
import { formatDate, normalizeStr } from '../../../services';
import { formatPhoneNumber } from '../cards/cards';

export const formatPersonName = (
    first?: string,
    last?: string,
    suffix?: string,
    organization?: string,
): string => {
    if (organization && !last) {
        return organization;
    }

    const base = _([first, last]).compact().join(' ');
    return base ? _([base, suffix]).compact().join(' ') : '';
};

export const formatAddress = (addresses?: Address[]): string | undefined => {
    const address = _.first(addresses);
    if (!address) {
        return;
    }

    const parts: string[] = [];

    if (address.lines?.length) {
        parts.push(...address.lines);
    }

    const cityStatePostal = _.compact([address.city, address.state, address.postal]).join(', ');
    if (cityStatePostal) {
        parts.push(cityStatePostal);
    }

    return parts.join(', ');
};

export const formatPatientDemographics = (patient?: ProviderPatient | SupportPatient): string | undefined => {
    if (!patient) {
        return;
    }

    const age = patient.dob ? differenceInYears(new Date(), new Date(patient.dob)) : undefined;

    return _.compact([
        patient.sex ? _.upperFirst(_.lowerCase(patient.sex)) : undefined,
        patient.dob ? `Born ${formatDate(patient.dob, 'MMMM d, yyyy')}` : undefined,
        age !== undefined ? `${age} yrs` : undefined,
    ]).join(' · ');
};

export const formatSubscriberSummary = (
    subscriber?: ProviderSubscriber | SupportSubscriber,
): string | undefined => {
    if (!subscriber) {
        return;
    }

    const name = formatPersonName(subscriber.first, subscriber.last, subscriber.suffix, subscriber.organization);
    const sex = subscriber.sex ? _.upperFirst(_.lowerCase(subscriber.sex)) : undefined;
    const dob = subscriber.dob ? `Born on ${formatDate(subscriber.dob, 'MMM d, yyyy')}` : undefined;

    return _.compact([name, sex, dob]).join(' · ') || undefined;
};

export const formatPayerTitle = (payer?: ProviderPayer | SupportPayer): string | undefined => {
    if (!payer) {
        return;
    }

    return payer.name;
};

/** Coverage and optional payer description for the card subtitle. */
export const formatPayerDescription = (payer?: ProviderPayer | SupportPayer): string | undefined => {
    if (!payer) {
        return;
    }

    let description = payer.description || '';
    if (payer.coverage) {
        description += `${description ? ' ' : ''}(${normalizeStr(payer.coverage)})`;
    }
    return description || undefined;
};

export const formatGuarantorName = (guarantor?: ProviderGuarantor | SupportGuarantor): string | undefined => {
    if (!guarantor) {
        return;
    }

    return formatPersonName(guarantor.first, guarantor.last, guarantor.suffix, guarantor.organization);
};

export const formatRelationshipLabel = (
    subscriberPatient?: ProviderSubscriberPatient | SupportSubscriberPatient,
): string | undefined => {
    if (!subscriberPatient?.relationship) {
        return;
    }

    return `Relationship · ${_.upperFirst(_.lowerCase(subscriberPatient.relationship))}`;
};

export const formatPhone = (phone?: string): string | undefined => {
    if (!phone) {
        return;
    }

    return formatPhoneNumber(phone) || phone;
};
